import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { useWatch, type UseFormReturn } from "react-hook-form";
import { memo } from "react";
import { TagBadge } from "./TagBadge";

/** Dados do formulário de criação/edição de tag. Mapeado 1:1 com `TagRequestDTO`. */
export interface TagFormData {
  name: string;
  description?: string;
  /** Cor em hexadecimal, ex: `"#3B82F6"`. */
  color: string;
}

interface TagFormDialogProps {
  open: boolean;
  mode: "create" | "edit";
  loading: boolean;
  /** Instância do form criada na página pai via `useForm<TagFormData>`. */
  form: UseFormReturn<TagFormData>;
  onClose: () => void;
  onSubmit: (data: TagFormData) => void;
}

/**
 * Dialog de criação e edição de tags.
 *
 * Exibe um color picker nativo sincronizado com um input de texto hex e uma
 * pré-visualização ao vivo do `TagBadge`. A preview usa `useWatch` em vez de
 * `form.watch` para garantir re-render reativo a cada mudança de campo.
 *
 * O form é controlado pela página pai — o componente não instancia nem reseta
 * o estado do formulário.
 *
 * @example
 * <TagFormDialog
 *   open={dialogMode === "create"}
 *   mode="create"
 *   loading={loading}
 *   form={form}
 *   onClose={handleClose}
 *   onSubmit={onSubmit}
 * />
 */
export const TagFormDialog = memo(function TagFormDialog({
  open,
  mode,
  loading,
  form,
  onClose,
  onSubmit,
}: TagFormDialogProps) {
  const watchedName = useWatch({ control: form.control, name: "name" });
  const watchedColor = useWatch({ control: form.control, name: "color" });

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Nova Tag" : "Editar Tag"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Preencha os dados da nova tag"
              : "Atualize as informações da tag"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Premium" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descrição opcional da tag..."
                      className="resize-none"
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cor *</FormLabel>
                  <div className="flex items-center gap-3">
                    <FormControl>
                      <input
                        type="color"
                        className="h-10 w-12 cursor-pointer rounded-md border border-input p-1"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onInput={(e) =>
                          field.onChange((e.target as HTMLInputElement).value)
                        }
                      />
                    </FormControl>
                    <Input
                      placeholder="#FF5733"
                      value={field.value}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val);
                      }}
                      maxLength={7}
                      className="font-mono uppercase"
                    />
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {watchedName && watchedColor && (
              <div className="pt-1">
                <p className="text-xs text-muted-foreground mb-2">
                  Pré-visualização
                </p>
                <TagBadge tag={{ name: watchedName, color: watchedColor }} />
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading
                  ? "Salvando..."
                  : mode === "create"
                    ? "Criar Tag"
                    : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
